import { FC } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { ProgramPoints } from '@rikstv/play-player/src/components/programPoints/ProgramPoints';
import { Spinner } from '@rikstv/play-player/src/components/Spinner/Spinner';
import { selectViewMode } from '@rikstv/play-player/src/forces/selectors';
import { Gutter } from '@rikstv/shared-components';

import { commonRoutes } from '../../router/router.path';
import { AssetNotFound } from '../errorPage/AssetNotFound';
import { TvGuideMini } from '../tvGuide-v2/mini/TvGuideMini';
import { useTvGuideChannels } from '../tvGuide-v2/useTvGuideChannels';

import { PreviouslyAiredGrid } from './archive/PreviouslyAiredGrid';
import { ProgramDetailsItem } from './linearPrograms/ProgramDetailsItem';
import { ExternalChannelPoster } from './live/ExternalChannelPoster';
import { useLiveChannel } from './live/useLiveChannel';
import { CoverPage } from './CoverPage';
import { PlayerDialogues } from './coverpage.types';
import { PlaybackLive } from './PlaybackLive';

interface Props extends PlayerDialogues {
  channelId: string;
}

export const CoverPageChannel: FC<Props> = ({ channelId, noAccessDialogue, signupDialogue }) => {
  const isValidChannelId = /^\d+$/.test(channelId);

  const { channel, isLoading, currentProgram } = useLiveChannel(isValidChannelId ? channelId : undefined);

  const viewMode = useSelector(selectViewMode);

  if (!isValidChannelId) {
    // might have ended up here with channel name instead of id, so attempt to resolve and redirect
    return <ChannelNameRedirector channelName={channelId} />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (!channel) {
    return <AssetNotFound contextInfo={`CoverPageChannel channelId=${channelId}`} />;
  }

  const image = `${currentProgram?.image || channel.posterUrl}?width=800`;
  const titlePrefix = channel.isExternal ? 'Direktesending på ' : '';
  const title = `${titlePrefix}${channel.name}`;

  return (
    <CoverPage
      canonicalUrl={commonRoutes.watchLive(channel.id, channel.name)}
      viewMode={viewMode}
      image={image}
      title={title}
      descriptionComponent={<ProgramDetailsItem program={currentProgram} isExternal={channel.isExternal} />}
      microdata={{
        itemType: 'http://schema.org/BroadcastService',
        itemPropImage: 'broadcaster',
        itemPropType: 'channel',
      }}
      metatitle={channel.name}
      playerComponent={
        channel.isExternal ? (
          <ExternalChannelPoster channel={channel} poster={image} />
        ) : (
          <PlaybackLive
            noAccessDialogue={noAccessDialogue}
            signupDialogue={signupDialogue}
            channel={channel}
            posterUrl={image}
          />
        )
      }
      programPointsComponent={<ProgramPoints channel={channel} />}>
      <section className="next">
        <TvGuideMini currentChannel={channel} />
      </section>
      <Gutter>
        <section className="archive">
          <PreviouslyAiredGrid name={channel.name} archiveUrl={channel.archiveUrl} />
        </section>
      </Gutter>
    </CoverPage>
  );
};

const ChannelNameRedirector: FC<{ channelName: string }> = ({ channelName }) => {
  const { channels, isLoading } = useTvGuideChannels({ includePrograms: false });

  if (isLoading) {
    return <Spinner />;
  }

  const channel = channels?.find(c => c.serviceName.toLowerCase() === channelName);
  if (!channel) {
    return <AssetNotFound contextInfo={`ChannelNameRedirector, channelName=${channelName}}`} />;
  }

  return <Navigate to={commonRoutes.watchLive(channel.channelId, channel.serviceName)} replace={true} />;
};
